import { Component, OnInit } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GlobalSearchApiService } from './global-search.apiservice';
import { ActivatedRoute, Router } from '@angular/router';
import { CAMPAIGN_STAGES } from '../../core/helpers/constants';
import { AppService } from '../../store/app.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss']
})

export class GlobalSearchComponent implements OnInit {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  result: Map<string, any[]> = new Map();
  keyword: string = '';
  constructor(private searchApiService: GlobalSearchApiService,
    private route: ActivatedRoute,
    private router: Router,
    private appService: AppService,
    private translate: TranslateService) { }

  ngOnInit(): void {
    this.appService.setPageTitle$('SEARCH');
    this.columns.prospects = [...this.columns.clients];

    this.route.params.subscribe(params => {
      this.keyword = params['keyword'];
      this.search(this.keyword);
    });
  }

  columns: any = {
    "clients": [
      { "field": "firstname", "title": "FIRST_NAME" },
      { "field": "lastname", "title": "LAST_NAME" },
      { "field": "brand", "title": "COMPANY" },
      { "field": "dmsname", "title": "DMS" },
      { "field": "salesrep", "title": "SALES_REP" },
      { "field": "phone", "title": "PHONE" },
      { "field": "email", "title": "EMAIL" }
    ],
    "campaigns": [
      { "field": "name", "title": "NAME" },
      { "field": "clientname", "title": "CLIENT_NAME" },
      { "field": "platform", "title": "PLATFORM" },
      { "field": "dms", "title": "DMS" },
      { "field": "stage", "title": "STAGE", "transform": this.getStageName.bind(this) },
      { "field": "subscriptionactive", "title": "ACTIVE_SUBSCRIPTION", "transform": this.getActiveSubscription.bind(this) }
    ],
    "tags": [
      { "field": "name", "title": "NAME" },
      { "field": "description", "title": "DESCRIPTION" },
      { "field": "categories", "title": "CATEGORIES" }
    ],
    "companies": [
      { "field": "companyname", "title": "NAME" },
      { "field": "firstname", "title": "FIRST_NAME" },
      { "field": "lastname", "title": "LAST_NAME" },
      { "field": "email", "title": "EMAIL" }
    ],
    "users": [
      { "field": "firstname", "title": "FIRST_NAME" },
      { "field": "lastname", "title": "LAST_NAME" },
      { "field": "email", "title": "EMAIL" }
    ],
    "leads": [
      { "field": "firstname", "title": "FIRST_NAME" },
      { "field": "lastname", "title": "LAST_NAME" },
      { "field": "email", "title": "EMAIL" },
      { "field": "phone", "title": "PHONE" },
      { "field": "campaignname", "title": "CAMPAIGN_NAME" },
      { "field": "referralid", "title": "REFERRAL_ID" },
      { "field": "referralsource", "title": "REFERRAL_SOURCE" },
    ],
    "tasks": [
      { "field": "title", "title": "TITLE" },
      { "field": "clientname", "title": "CLIENT_NAME" },
      { "field": "campaignname", "title": "CAMPAIGN_NAME" },
      { "field": "duedate", "title": "DUE_DATE" },
      { "field": "assignedusername", "title": "ASSIGNED_USER" },
    ],
    "subscriptions": [
      { "field": "numericid", "title": "#" },
      { "field": "product", "title": "PRODUCTS" },
      { "field": "clientname", "title": "CLIENT_NAME" },
      { "field": "startdate", "title": "START_DATE", pipe: 'P_AppDateFormat', pipeArgs: ['MM/DD/YYYY'] },
      { "field": "status", "title": "STATUS" },
      { "field": "campaigns", "title": "CAMPAIGNS" },
    ]
  }

  search(keyword: string) {
    this.result.clear();
    const that = this;
    console.log("Searching for keyword:", keyword);
    this.searchApiService.getSearchResults(keyword)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        console.log("Response:", response);
        console.log("that.columns:", that.columns);
        that.result = that.processSearchResponse(response, that.columns);
        console.log("Processed Result:", that.result);
      });
  }

  processSearchResponse(response: any, columns: any): Map<string, any[]> {
    response.data = this.onlyUnique(response.data);
    var result: Map<string, any[]> = new Map();
    for (let i = 0; i < response.data.length; i++) {
      const responseItem = response.data[i];
      const type = responseItem.estype;
      const definition = columns[type];
      if (!result.has(type)) {
        result.set(type, []);
      }
      let row: any = {};
      Object.keys(responseItem).forEach((key: string) => {
        var lowerCaseKey = key.toLowerCase();
        if (definition.find((x: any) => x.field === lowerCaseKey)?.transform) {
          row[lowerCaseKey] = definition.find((x: any) => x.field === lowerCaseKey).transform(responseItem);
        } else {
          row[lowerCaseKey] = responseItem[key];
        }
      });
      result.get(type)?.push(row);
    }
    return result;
  }

  onlyUnique(arr: Array<any>) {
    arr = arr.map((x: any) => { return { ...x, id: x.id.split('_')[0] } });
    arr = arr.filter((v, i, a) => a.findIndex(v2 => (v2.id === v.id)) === i);
    return arr;
  }

  getLink(entity: any) {
    if (entity.estype === 'clients') { return `/pages/client-details/${entity.id}`; }
    else if (entity.estype === 'campaigns') { return `/pages/campaign-details/${entity.id}`; }
    else if (entity.estype === 'tags') { return `/pages/tags/`; }
    else if (entity.estype === 'companies') { return `/pages/companies/`; }
    else if (entity.estype === 'users') { return `/pages/users/`; }
    else if (entity.estype === 'leads') { return `/pages/leads/details/${entity.id}`; }
    else if (entity.estype === 'tasks') { return `/pages/tasks`; }
    else if (entity.estype === 'subscriptions') { return `/pages/subscriptions/${entity.id}`; }
    else if (entity.estype === 'prospects') { return `/pages/prospect-details/${entity.id}`; }
    return null;
  }

  onRowClick(evt: any) {
    const link = this.getLink(evt);
    this.router.navigate([link]);
  }

  getStageName(item: any) {
    const stage = CAMPAIGN_STAGES.find(x => x.value === item.Stage);
    return stage ? this.translate.instant(stage.translate) : '';
  }

  getActiveSubscription(item: any) {
    return item.subscriptionactive ? this.translate.instant('YES') : this.translate.instant('NO');
  }
}
